import { useState } from 'react';
import { cn } from '@/lib/utils';
import { SectionHeading } from '@/components/ui/SectionHeading';

const FAQS = [
  {
    q: 'How does the discount decay work?',
    a: 'When a merchant publishes a flash deal, it starts at a high discount (e.g. 50% off). Over time, the discount drops through tiers — 50%, then 40%, then 20%, then 10% — until the deal expires. The faster you arrive at the store, the higher the discount you lock in.',
  },
  {
    q: 'How do I claim a deal?',
    a: 'When you arrive at the store, open the deal in the app and tap "Claim." A QR code is generated that the cashier scans. The discount locks in at whatever tier is active at the moment you claim — not when you first saw the notification.',
  },
  {
    q: 'Can I share my deal with someone else?',
    a: 'No. Each claim is tied to your account and generates a unique, time-limited QR code. Screenshots or forwarded codes won\'t work. This protects merchants from coupon abuse and keeps deals scarce.',
  },
  {
    q: 'Is Run2Sale free for shoppers?',
    a: 'Yes, completely free. You see deals, get notifications, race to the store, and claim discounts — all at no cost. Merchants pay to publish deals, not shoppers.',
  },
  {
    q: 'How does it work for merchants?',
    a: 'Merchants set a deal (product, starting discount, decay schedule, number of slots), and publish it. Nearby shoppers get notified. The merchant dashboard shows real-time views, claims, and conversion rates. You control the discount curve, duration, and slot limit.',
  },
  {
    q: 'When is Run2Sale launching?',
    a: 'We\'re currently in private beta. Join the waitlist to get early access when we launch in your area. Early waitlist members will get exclusive perks at launch.',
  },
];

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-6 py-5 text-left"
      >
        <span className="pr-4 text-base font-semibold text-gray-900">{question}</span>
        <svg
          width={20}
          height={20}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          className={cn(
            'flex-shrink-0 text-gray-400 transition-transform duration-200',
            open && 'rotate-180',
          )}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          open ? 'max-h-96 pb-5' : 'max-h-0',
        )}
      >
        <p className="px-6 text-sm leading-relaxed text-gray-600">{answer}</p>
      </div>
    </div>
  );
}

export function Faq() {
  return (
    <section id="faq" className="bg-gray-50 py-24">
      <div className="container mx-auto max-w-2xl px-6">
        <SectionHeading
          eyebrow="FAQ"
          title="Questions? Answers."
        />
        <div className="mt-12 flex flex-col gap-4">
          {FAQS.map((faq) => (
            <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </div>
    </section>
  );
}
